<!-- Responsibility: Lean orchestrator for top-level layout and always-mounted global surfaces.
Scope: Route hosting and manager bootstrap only; dialog/system content orchestration lives elsewhere. -->
<app-shell>
  <system-bar></system-bar>

  <main>
    <div class="page-host">
      <div is={ state.currentPage }></div>
    </div>
  </main>

  <modal></modal>
  <error-view></error-view>
  <feedback-form></feedback-form>

  <script>
    import { stateService, configureAppStates, dispatcher } from '../app-states.js'
    import { sessionManager } from '../session.js'
    import { initPwa } from '../pwa.js'
    import { themeManager } from '../theme-manager.js'
    import { initAudio } from '../engine/audio.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { setupDecoratedTooltips } from './helpers/decorated-tooltips.ts'

    import LoginView from './screens/login-view.riot'
    import WelcomeView from './screens/welcome-view.riot'
    import HubView from './screens/hub-view.riot'
    import MarketplaceView from './screens/marketplace-view.riot'
    import SavesView from './screens/saves-view.riot'
    import WorldsView from './screens/worlds-view.riot'
    import VesselsView from './screens/vessels-view.riot'
    import PairingView from './screens/pairing-view.riot'
    import ForgingView from './screens/forging-view.riot'
    import AdminDashboardView from './screens/admin-dashboard-view.riot'
    import DevObservabilityView from './screens/dev-observability-view.riot'
    import SceneView from './screens/scene-view.riot'

    import SystemBar from './components/system-bar.riot'
    import Modal from './components/dialog/modal.riot'
    import ErrorView from './screens/error-view.riot'
    import FeedbackForm from './components/feedback-form.riot'

    export default {
      components: {
        SystemBar, Modal, ErrorView, FeedbackForm,
        LoginView, WelcomeView, HubView, MarketplaceView, SavesView, WorldsView,
        VesselsView, PairingView, ForgingView, AdminDashboardView,
        DevObservabilityView, SceneView,
      },

      state: {
        currentPage: 'login-view',
      },

      onMounted() {
        this.activeOverlayKeys = new Set()
        configureAppStates()
        initPwa()
        initAudio()
        themeManager.init()

        this.navHandle = stateService.onChange((_event, changedState) => {
          const ctx = stateService.context
          this.update({
            currentPage: changedState?.pageName ?? 'login-view',
          })
        })

        this.overlayHandles = [
          dispatcher.on(TOPICS.ui.overlayOpened, (_event, detail) => this.trackOverlay(detail?.key, true)),
          dispatcher.on(TOPICS.ui.overlayClosed, (_event, detail) => this.trackOverlay(detail?.key, false)),
        ]

        stateService.start()
        // Start routing before access enforcement so signed-in deep links like /new or /play/:id are not collapsed to hub.
        sessionManager.init()
        this.cleanupDecoratedTooltips = setupDecoratedTooltips()
      },

      onUnmounted() {
        this.cleanupDecoratedTooltips?.()
        this.overlayHandles?.forEach((handle) => dispatcher.off(handle))
        this.activeOverlayKeys?.clear()
        this.syncOverlayDocumentState()
        this.navHandle?.off()
        stateService.stop()
      },

      trackOverlay(key, isOpen) {
        const overlayKey = String(key ?? '').trim()
        if (!overlayKey || !this.activeOverlayKeys) return
        if (isOpen) this.activeOverlayKeys.add(overlayKey)
        else this.activeOverlayKeys.delete(overlayKey)
        this.syncOverlayDocumentState()
      },

      syncOverlayDocumentState() {
        const hasOpenOverlay = !!this.activeOverlayKeys?.size
        document.documentElement.classList.toggle('am2-modal-open', hasOpenOverlay)
        document.body.style.overflow = hasOpenOverlay ? 'hidden' : ''
      },
    }
  </script>

  <style>
    :host {
      display: grid;
      grid-template-rows: auto 1fr;
      height: 100dvh;
      width: 100%;
      overflow: hidden;
      background: var(--bg);
    }

    main {
      position: relative;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .page-host {
      min-height: 100%;
      width: 100%;
    }
  </style>
</app-shell>
